/**
 * Toast helpers for surfacing API errors as global notifications.
 *
 * For mutations that already render their error inline (e.g. via
 * `<FormErrorBanner>`), pass `meta: { silent: true }` to the mutation so the
 * QueryClient-level handler skips the toast.
 */
import { toast } from 'sonner';
import i18n from '@/i18n';
import { ApiError } from '@/lib/api';
import {
  errorKeyFor as defaultResolver,
  formatErrorMessage,
  type ErrorResolver,
} from '@/lib/form/errorMessages';

export type { ErrorResolver };

/**
 * Surface an error as a toast that says what went wrong (via the given
 * resolver). A request the server answered carries its reference underneath,
 * the id its logs know it by.
 */
export function toastError(err: unknown, resolveKey: ErrorResolver = defaultResolver): void {
  const message = formatErrorMessage(i18n.t, resolveKey(err));
  const reference =
    err instanceof ApiError && err.requestId
      ? i18n.t('errors.reference', { requestId: err.requestId })
      : undefined;
  toast.error(message, reference ? { description: reference } : undefined);
}

export function toastSuccess(messageOrKey: string, values: Record<string, unknown> = {}): void {
  toast.success(i18n.t(messageOrKey, { ...values, defaultValue: messageOrKey }));
}
